import React, { useEffect, useState } from "react";
import { TMenu, TPrivilege, TUser } from "../types/config";
import { eResultCode } from "../utils/enum";
import AuthUtil from "../utils/auth";
import Utils from "../utils";
import useFetch from "../hooks/useFetch";
import {
  GetChunkAPICountDtl,
  GetMenuHierarchyList,
  GetSpecificUserMasterData,
} from "../utils/api.constant";
import { StoreUtil } from "../utils/store";
import { LOGGED_USER_ID } from "../utils/constants";
import Aes from "../utils/aes.encrypt.decrypt";

type TProps = {
  children: React.ReactNode;
};

type TState = {
  value: {
    onUpdate: Function | undefined;
    isUserLogged: boolean;
  };
};

const UserContext = React.createContext<{
  onUpdate?: Function;
  user?: TUser;
  userName?: string;
  email?: string;
  orgid?: string;
  isUserLogged?: boolean;
  privilegeList?: Array<TPrivilege>;
  isTempPassword?: Boolean;
  defLanguageUniqueId?: string;
  menuHierarchy?: Array<TMenu>;
  chunkSize?: number;
  apiCount?: number;
  currency?: string;
  numberFormat?: string;
}>({});

export default function UserProvider(props: TProps) {
  const { post } = useFetch();

  // Function to update the user context state
  const onUpdate = (detail: any = {}) => {
    setState((prevState: any) => ({
      value: {
        ...prevState.value,
        ...detail,
        user: { ...prevState.value.user, ...detail.user },
        isUserLogged: !Utils.isNullOrUndefined(detail.id ?? prevState.value.id),
      },
    }));
  };
  useEffect(() => {
    init();
  }, []);

  const [state, setState] = useState<TState>({
    value: {
      onUpdate: onUpdate,
      isUserLogged: false,
    },
  });

  // Initialization function called on component mount
  async function init() {
    if (AuthUtil.isTokenExist()) {
      const user: TUser = await initializeUser();

      const menuHierarchy: any = await getMenuHierarchyList(); // Fetch menu data
      const chunkConfig: any = await getChunkConfiguration(); // Fetch chunk configuration
      onUpdate({
        user: user,
        menuHierarchy: menuHierarchy,
        chunkSize: chunkConfig?.chunkSize,
        apiCount: chunkConfig?.apiCount,
      });
    }
  }

  // Function to fetch user information from the server
  const initializeUser = async () => {
    try {
      const requestPayload = {
        data: {},
      };
      const response = await post(GetSpecificUserMasterData, requestPayload);
      const { data, dataResponse } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        const value = data[0];
        StoreUtil.setCookie(
          LOGGED_USER_ID,
          Aes.encryptURLSafe(value?.id?.toString())
        );
        // StoreUtil.setCookie(LOGGED_USER_ID, value?.id);

        return value;
      } else {
        AuthUtil.logout();
        console.error(`Error while initializing user: ${description}`);
      }
    } catch (error) {
      AuthUtil.logout();
      console.error(error);
    }
  };

  // Function to fetch menu hierarchy list from the server
  const getMenuHierarchyList = async () => {
    try {
      const requestPayload = {
        data: {
          renderMenuRoleWise: true,
        },
      };
      const response = await post(GetMenuHierarchyList, requestPayload);
      const { data, dataResponse } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        return data;
      } else {
        console.error(`Error while fetching menu hierarchy: ${description}`);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getChunkConfiguration = async () => {
    try {
      const requestPayload = {
        data: {},
      };
      const response = await post(GetChunkAPICountDtl, requestPayload);
      const { data, dataResponse } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        return data;
      } else {
        console.error(`Error while chunk configuration: ${description}`);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Render the UserContext.Provider with the context value and children
  return (
    <UserContext.Provider value={state.value}>
      {props.children}
    </UserContext.Provider>
  );
}

// Export the UserContext and UserProvider for use in other components
export { UserContext, UserProvider };
